<template>
  <template v-if="show && frameBuffer.length >= 2 && (currentWidget || nextWidget)">
    <!-- Slot 0: positioned to buffer index 0's section/container (prepositioned). Fall back to any rect so widget renders when refs are not yet set. -->
    <div
      v-if="anyOverlayStyle && slot0Widget"
      class="z-20 pointer-events-none overflow-hidden"
      :class="[
        visibleSlot === 0 ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        crossfadeMs > 0 ? 'transition-opacity ease-linear' : ''
      ]"
      :style="{
        ...(overlayStyle0 ?? anyOverlayStyle),
        ...(crossfadeMs > 0 ? { transitionDuration: `${crossfadeMs}ms` } : {})
      }"
    >
      <div class="relative h-full w-full">
        <WidgetIframeHost
          :key="`slot0-${slot0Widget.urn}-${slot0Widget.src}`"
          :src="slot0Widget.src"
          :asset-urn="slot0Widget.urn"
          :config="slot0Config"
          :widget-width="slot0Widget.width"
          :widget-height="slot0Widget.height"
          :show="visibleSlot === 0"
          class="w-full h-full"
          @iframe-loaded="(urn: string) => onAssetIframeLoaded?.(urn)"
          @widget-ready="onSlotReady(0)"
        />
      </div>
    </div>
    <!-- Slot 1: positioned to buffer index 1's section/container (prepositioned). Fall back to any rect so widget renders when refs are not yet set. -->
    <div
      v-if="anyOverlayStyle && slot1Widget"
      class="z-20 pointer-events-none overflow-hidden"
      :class="[
        visibleSlot === 1 ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        crossfadeMs > 0 ? 'transition-opacity ease-linear' : ''
      ]"
      :style="{
        ...(overlayStyle1 ?? anyOverlayStyle),
        ...(crossfadeMs > 0 ? { transitionDuration: `${crossfadeMs}ms` } : {})
      }"
    >
      <div class="relative h-full w-full">
        <WidgetIframeHost
          :key="`slot1-${slot1Widget.urn}-${slot1Widget.src}`"
          :src="slot1Widget.src"
          :asset-urn="slot1Widget.urn"
          :config="slot1Config"
          :widget-width="slot1Widget.width"
          :widget-height="slot1Widget.height"
          :show="visibleSlot === 1"
          class="w-full h-full"
          @iframe-loaded="(urn: string) => onAssetIframeLoaded?.(urn)"
          @widget-ready="onSlotReady(1)"
        />
      </div>
    </div>
  </template>
</template>

<script lang="ts" setup>
import type { ComputedRef, Ref } from 'vue'
import type { IActiveWidgetPayload } from '@/helpers/widgetSlotHelpers'
import type { IAssetIndex, IQueueFrame } from '@/types'
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue'
import WidgetIframeHost from '@/components/WidgetIframeHost.vue'

import { getActiveWidgetForFrame } from '@/helpers/widgetSlotHelpers'

const props = withDefaults(
  defineProps<{
    frameBuffer: IQueueFrame[]
    viewIdx: number
    assetIndex: IAssetIndex
    /** All frames in the campaign (for config resolution); can use frameBuffer when length >= 2. */
    allFrames?: IQueueFrame[]
    show: boolean
    /** When &gt; 0, opacity-swap between slots uses this dissolve duration (Phase C / Studio crossfade). */
    crossfadeMs?: number
  }>(),
  { allFrames: () => [], crossfadeMs: 0 }
)

const widgetSectionRefForIndex0 = inject<Ref<HTMLElement | null>>('widgetSectionRefForIndex0', ref(null))
const widgetSectionRefForIndex1 = inject<Ref<HTMLElement | null>>('widgetSectionRefForIndex1', ref(null))
const frameContainerRefForIndex0 = inject<Ref<HTMLElement | null>>('frameContainerRefForIndex0', ref(null))
const frameContainerRefForIndex1 = inject<Ref<HTMLElement | null>>('frameContainerRefForIndex1', ref(null))
const onAssetIframeLoaded = inject<(urn: string) => void>('onAssetIframeLoaded')
/** Platform-injected fields merged into widget config (from VBuffer). */
const platformWidgetConfig = inject<ComputedRef<Record<string, unknown>>>('platformWidgetConfig', computed(() => ({})))
const reportHoldPaintReady = inject<((info: { type: 'widget' }) => void) | null>(
  'reportHoldPaintReady',
  null
)
const rectForSlot0 = ref<DOMRect | null>(null)
const rectForSlot1 = ref<DOMRect | null>(null)
const lastRect0 = ref<DOMRect | null>(null)
const lastRect1 = ref<DOMRect | null>(null)
let resizeObserver0: ResizeObserver | null = null
let resizeObserver1: ResizeObserver | null = null

function getRectSourceForSlot(slotIndex: 0 | 1): HTMLElement | null {
  const section = slotIndex === 0 ? widgetSectionRefForIndex0?.value : widgetSectionRefForIndex1?.value
  const container = slotIndex === 0 ? frameContainerRefForIndex0?.value : frameContainerRefForIndex1?.value
  return section ?? container ?? null
}

function updateRectForSlot(slotIndex: 0 | 1) {
  const el = getRectSourceForSlot(slotIndex)
  const rect = el ? el.getBoundingClientRect() : null
  const rectRef = slotIndex === 0 ? rectForSlot0 : rectForSlot1
  const lastRef = slotIndex === 0 ? lastRect0 : lastRect1
  rectRef.value = rect
  if (rect) { lastRef.value = rect }
}

function attachObservers() {
  const el0 = getRectSourceForSlot(0)
  const el1 = getRectSourceForSlot(1)
  if (resizeObserver0) { resizeObserver0.disconnect(); resizeObserver0 = null }
  if (resizeObserver1) { resizeObserver1.disconnect(); resizeObserver1 = null }
  if (!el0) { rectForSlot0.value = null }
  else { updateRectForSlot(0); resizeObserver0 = new ResizeObserver(() => updateRectForSlot(0)); resizeObserver0.observe(el0) }
  if (!el1) { rectForSlot1.value = null }
  else { updateRectForSlot(1); resizeObserver1 = new ResizeObserver(() => updateRectForSlot(1)); resizeObserver1.observe(el1) }
}

watch(
  () => [
    widgetSectionRefForIndex0?.value,
    widgetSectionRefForIndex1?.value,
    frameContainerRefForIndex0?.value,
    frameContainerRefForIndex1?.value
  ] as const,
  attachObservers,
  { immediate: true }
)

onBeforeUnmount(() => {
  if (resizeObserver0) { resizeObserver0.disconnect(); resizeObserver0 = null }
  if (resizeObserver1) { resizeObserver1.disconnect(); resizeObserver1 = null }
})

/** 1px overlap upward to hide seam at video/widget junction. Use integer pixels so overlay aligns with section and avoids subpixel gaps on Android WebView. */
const OVERLAP_TOP_PX = 1

function overlayStyleFromRect(r: DOMRect): Record<string, string> {
  const left = Math.floor(r.left)
  const top = Math.floor(r.top) - OVERLAP_TOP_PX
  const width = Math.ceil(r.width)
  const height = Math.ceil(r.height) + OVERLAP_TOP_PX
  return {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`
  }
}

const overlayStyle0 = computed((): Record<string, string> | undefined => {
  const r = rectForSlot0.value ?? lastRect0.value
  if (!r) { return undefined }
  return overlayStyleFromRect(r)
})
const overlayStyle1 = computed((): Record<string, string> | undefined => {
  const r = rectForSlot1.value ?? lastRect1.value
  if (!r) { return undefined }
  return overlayStyleFromRect(r)
})

/** At least one overlay style (from either slot) so the visible widget can render even when only one ref is set. */
const anyOverlayStyle = computed((): Record<string, string> | undefined =>
  overlayStyle0.value ?? overlayStyle1.value ?? undefined
)

/** Current and next frame from buffer (buffer length must be >= 2). */
const currentFrame = computed(() => {
  const buf = props.frameBuffer
  const idx = props.viewIdx
  if (buf.length < 2 || idx < 0 || idx >= buf.length) { return null }
  return buf[idx] ?? null
})

const nextFrame = computed(() => {
  const buf = props.frameBuffer
  const idx = props.viewIdx
  if (buf.length < 2 || idx < 0 || idx >= buf.length) { return null }
  return buf[1 - idx] ?? null
})

/** Active widget for current and next frame (first widget facet by section order). */
const currentWidget = computed(() => getActiveWidgetForFrame(currentFrame.value, props.assetIndex))
const nextWidget = computed(() => getActiveWidgetForFrame(nextFrame.value, props.assetIndex))

/** Frame two steps ahead in the queue (for prefetch). */
const nextNextFrame = computed(() => {
  const cur = currentFrame.value
  const all = props.allFrames ?? []
  if (!cur || all.length < 2) { return null }
  const idx = all.findIndex(f => f.playlistSeqNo === cur.playlistSeqNo)
  if (idx < 0) { return null }
  return all[(idx + 2) % all.length] ?? null
})
const nextNextWidget = computed(() => getActiveWidgetForFrame(nextNextFrame.value, props.assetIndex))

/** Prefetch widget URL so the slot iframe gets a cache hit when it loads. Same-origin http(s) only. */
const prefetchedUrls = new Set<string>()
function prefetchWidgetUrl(url: string | undefined): void {
  if (!url || url.startsWith('blob:') || url === 'about:blank') { return }
  try {
    const u = new URL(url, window.location.origin)
    if (u.origin !== window.location.origin || (!u.protocol.startsWith('http'))) { return }
    if (prefetchedUrls.has(url)) { return }
    prefetchedUrls.add(url)
    fetch(url, { mode: 'same-origin' }).catch(() => {})
  }
  catch {
    // ignore invalid URLs
  }
}

/** Which physical slot is visible (0 or 1). Kept in sync with viewIdx so the correct frame's widget is always shown. */
const visibleSlot = ref(0)

const slot0Ready = ref(false)
const slot1Ready = ref(false)

/** Sync visible slot to viewIdx immediately so the widget shown always matches the current frame (no ready-gate lag). */
function tryApplyVisibleSlot() {
  const target = props.viewIdx
  if (target < 0 || target > 1) { return }
  if (visibleSlot.value !== target) {
    visibleSlot.value = target
  }
}

function onSlotReady(slotIndex: 0 | 1) {
  if (slotIndex === 0) {
    slot0Ready.value = true
  }
  else {
    slot1Ready.value = true
  }
  // Incoming widget paint-ready → clear sticky hold (image/video → widget) via Phase C ready fade.
  if (slotIndex === props.viewIdx) {
    reportHoldPaintReady?.({ type: 'widget' })
  }
}

/** Slot contents. Updated only when the target widget for that slot changes (by urn+src) to avoid unnecessary reloads. */
const slot0Widget = ref<IActiveWidgetPayload | null>(null)
const slot1Widget = ref<IActiveWidgetPayload | null>(null)

/** Merged widget configs: user-authored config with platform fields spread last (platform fields take precedence). */
const slot0Config = computed<Record<string, unknown>>(() => ({
  ...(slot0Widget.value?.config ?? {}),
  ...platformWidgetConfig.value
}))
const slot1Config = computed<Record<string, unknown>>(() => ({
  ...(slot1Widget.value?.config ?? {}),
  ...platformWidgetConfig.value
}))

function payloadEquals(
  a: { urn: string, src: string } | null,
  b: { urn: string, src: string } | null
): boolean {
  if (a === b) { return true }
  if (!a || !b) { return false }
  return a.urn === b.urn && a.src === b.src
}

function widgetConfigJson(payload: IActiveWidgetPayload | null): string {
  return JSON.stringify(payload?.config ?? {})
}

/**
 * Same widget URL (urn+src) but editor changed merged config (e.g. RSS feedId) — update slot payload so
 * WidgetIframeHost's config watch sends CONFIG_UPDATE without forcing iframe reload.
 */
function syncSlotPayload(
  slotRef: Ref<IActiveWidgetPayload | null>,
  readyRef: Ref<boolean>,
  target: IActiveWidgetPayload | null
) {
  if (!payloadEquals(slotRef.value, target)) {
    slotRef.value = target
    readyRef.value = false
    return
  }
  if (
    target
    && slotRef.value
    && widgetConfigJson(slotRef.value) !== widgetConfigJson(target)
  ) {
    slotRef.value = target
  }
}

/** Sync slot contents and visible slot to viewIdx so the correct frame's widget is always shown. */
watch(
  [currentWidget, nextWidget, () => props.viewIdx],
  ([cur, nxt]) => {
    const idx = props.viewIdx
    if (props.frameBuffer.length < 2 || idx < 0 || idx > 1) { return }

    const targetForSlot0 = idx === 0 ? cur : nxt
    const targetForSlot1 = idx === 0 ? nxt : cur

    syncSlotPayload(slot0Widget, slot0Ready, targetForSlot0)
    syncSlotPayload(slot1Widget, slot1Ready, targetForSlot1)
    tryApplyVisibleSlot()
  },
  { immediate: true }
)

/** Prefetch next and next-next widget URLs so slot iframes get cache hits when they load. */
watch(
  [nextWidget, nextNextWidget],
  ([nxt, nxtNxt]) => {
    prefetchWidgetUrl(nxt?.src)
    prefetchWidgetUrl(nxtNxt?.src)
  },
  { immediate: true }
)
</script>
